import { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, authFetch } from "../App";
import { useProject } from "../context/ProjectContext";
import { toast } from "sonner";
import {
  format, startOfWeek, addDays, subDays, getISOWeek, getISOWeekYear,
  isSameDay, parseISO, getDay, startOfDay, differenceInDays,
} from "date-fns";
import { de } from "date-fns/locale";
import { AlertTriangle } from "lucide-react";

import {
  DEFAULT_DAILY_CAPACITY, RESIZE_SNAP, AUTO_SAVE_DELAY,
  getWeekDateRange, getMaxISOWeeks, getWeekMonday, ZOOM_LEVELS, VISIBLE_DAYS_BUFFER,
} from "../modules/planning/constants";
import PlanningHeader from "../modules/planning/PlanningHeader";
import PlanningGrid from "../modules/planning/PlanningGrid";
import {
  AssignmentDialog, QuickDeleteDialog, DeleteConfirmDialog,
  DiscardDialog, SaveTemplateDialog, ApplyTemplateDialog, PdfExportDialog,
  RecurringTemplatesDialog,
} from "../modules/planning/PlanningDialogs";

const API = '/api';

export default function Einsatzplanung() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { activeProject } = useProject();
  const { user } = useAuth();
  const scrollContainerRef = useRef(null);
  const headerScrollRef = useRef(null);
  const isGrabbingRef = useRef(false);
  const grabStartX = useRef(0);
  const grabScrollStartX = useRef(0);
  const autoSaveTimeoutRef = useRef(null);
  const saveInFlightRef = useRef(false);
  const fillDragRef = useRef(null);
  const employeeFilterRef = useRef(null);
  // Endlos-Scroll Helfer
  const extendingRef = useRef(false);
  const pendingPrependDaysRef = useRef(0);

  const isGlobalMode = !projectId;
  const [isGrabbing, setIsGrabbing] = useState(false);

  // Week state
  const [activeYear, setActiveYear] = useState(() => getISOWeekYear(new Date()));
  const [activeCalendarWeek, setActiveCalendarWeek] = useState(() => getISOWeek(new Date()));

  // Core state
  const [loading, setLoading] = useState(false);
  const [showSollIst, setShowSollIst] = useState(true);
  const [saveStatus, setSaveStatus] = useState("idle");

  // Data
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [originalAssignments, setOriginalAssignments] = useState([]);
  const [sollIstData, setSollIstData] = useState([]);
  const [loadedRange, setLoadedRange] = useState({ start: null, end: null });

  // Templates
  const [templates, setTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [applyTemplateDialogOpen, setApplyTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [targetWeekStart, setTargetWeekStart] = useState("");

  // Filters
  const [filterUserIds, setFilterUserIds] = useState([]);
  const [filterProjectId, setFilterProjectId] = useState("");
  const [employeeFilterOpen, setEmployeeFilterOpen] = useState(false);
  const [tempFilterUserIds, setTempFilterUserIds] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");

  // PDF
  const [pdfExportOpen, setPdfExportOpen] = useState(false);
  const [pdfExportLoading, setPdfExportLoading] = useState(false);
  const [pdfDateRange, setPdfDateRange] = useState({ start: "", end: "" });
  const [pdfUseFilters, setPdfUseFilters] = useState(true);

  // Recurring templates
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Multi-select & quick delete
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [quickDeleteTarget, setQuickDeleteTarget] = useState(null);

  // Form
  const [formData, setFormData] = useState({ user_id: "", project_id: projectId || "", planned_hours: 8, status: "planned", notes: "" });

  // Drag & Drop
  const [draggedAssignment, setDraggedAssignment] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [dragOverUserId, setDragOverUserId] = useState(null);
  const [isCopyDrag, setIsCopyDrag] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);

  // Resize
  const [resizingAssignment, setResizingAssignment] = useState(null);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartHours, setResizeStartHours] = useState(0);
  const [resizePreviewHours, setResizePreviewHours] = useState(0);

  // Fill Handle
  const [fillDragAssignment, setFillDragAssignment] = useState(null);
  const [fillPreviewCells, setFillPreviewCells] = useState([]);
  const [isFillDragging, setIsFillDragging] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  // Zoom level
  const [zoom, setZoom] = useState(() => localStorage.getItem("tga_planning_zoom") || "standard");
  const zoomConfig = ZOOM_LEVELS[zoom] || ZOOM_LEVELS.standard;
  const DAY_WIDTH = zoomConfig.dayWidth;

  // Pending changes
  const [pendingChanges, setPendingChanges] = useState({ modified: new Map(), created: new Map(), deleted: new Set() });

  const isAdmin = user?.role === "admin" || user?.role === "projektleiter" || user?.permissions?.can_approve_entries;
  const modifiedCount = pendingChanges.modified.size;
  const createdCount = pendingChanges.created.size;
  const deletedCount = pendingChanges.deleted.size;
  const totalChanges = modifiedCount + createdCount + deletedCount;
  const hasChanges = totalChanges > 0;
  const modifiedIds = new Set(pendingChanges.modified.keys());
  const newIds = new Set(pendingChanges.created.keys());
  const deletedIds = pendingChanges.deleted;

  // ============ DERIVED STATE ============
  const activeWeekMonday = useMemo(() => getWeekDateRange(activeYear, activeCalendarWeek).monday, [activeYear, activeCalendarWeek]);
  const activeWeekSunday = useMemo(() => addDays(activeWeekMonday, 6), [activeWeekMonday]);
  const dateRangeText = useMemo(() => `${format(activeWeekMonday, "d. MMM", { locale: de })} \u2013 ${format(activeWeekSunday, "d. MMM yyyy", { locale: de })}`, [activeWeekMonday, activeWeekSunday]);
  const currentWeekStart = useMemo(() => format(activeWeekMonday, "yyyy-MM-dd"), [activeWeekMonday]);

  // ============ ENDLOS-SCROLL FENSTER (User-Wunsch 05.05.2026) ============
  // Statt feste 21 Tage: dynamisches Fenster, das beim Scrollen an Rand erweitert wird.
  // Initial: 4 Wochen vor heute → 8 Wochen nach heute = 12 Wochen / 84 Tage.
  const WEEKS_INITIAL_BEFORE = 4;
  const WEEKS_INITIAL_AFTER = 8;
  const WEEKS_EXTEND_CHUNK = 4;        // Beim Erreichen des Rands: 4 Wochen nachladen
  const SCROLL_NEAR_EDGE_PX = 320;     // Schwelle für Auto-Extend
  const initialMonday = useMemo(() => getWeekMonday(new Date()), []);
  const [windowMonday, setWindowMonday] = useState(() => subDays(initialMonday, WEEKS_INITIAL_BEFORE * 7));
  const [windowSunday, setWindowSunday] = useState(() => addDays(initialMonday, WEEKS_INITIAL_AFTER * 7 - 1));

  const visibleDays = useMemo(() => {
    const days = [];
    const total = differenceInDays(windowSunday, windowMonday) + 1;
    for (let i = 0; i < total; i++) days.push(addDays(windowMonday, i));
    return days;
  }, [windowMonday, windowSunday]);

  const weekGroups = useMemo(() => {
    const groups = [];
    let currentWeek = null;
    let currentGroup = null;
    visibleDays.forEach((day, index) => {
      const weekNum = getISOWeek(day);
      const monday = getWeekMonday(day);
      const weekKey = format(monday, "yyyy-MM-dd");
      if (weekKey !== currentWeek) {
        if (currentGroup) groups.push(currentGroup);
        currentWeek = weekKey;
        currentGroup = { weekNum, monday, days: [day], startIndex: index, isActiveWeek: weekNum === activeCalendarWeek && getISOWeekYear(day) === activeYear };
      } else { currentGroup.days.push(day); }
    });
    if (currentGroup) groups.push(currentGroup);
    return groups;
  }, [visibleDays, activeCalendarWeek, activeYear]);

  // Update formData when projectId changes
  useEffect(() => { if (projectId) { setFormData(p => ({ ...p, project_id: projectId })); setFilterProjectId(projectId); } }, [projectId]);

  // ============ NAVIGATION ============
  const goToPreviousWeek = useCallback(() => {
    if (activeCalendarWeek > 1) setActiveCalendarWeek(activeCalendarWeek - 1);
    else { setActiveYear(activeYear - 1); setActiveCalendarWeek(getMaxISOWeeks(activeYear - 1)); }
  }, [activeYear, activeCalendarWeek]);

  const goToNextWeek = useCallback(() => {
    if (activeCalendarWeek < getMaxISOWeeks(activeYear)) setActiveCalendarWeek(activeCalendarWeek + 1);
    else { setActiveYear(activeYear + 1); setActiveCalendarWeek(1); }
  }, [activeYear, activeCalendarWeek]);

  const goToToday = useCallback(() => {
    const t = new Date();
    const newYear = getISOWeekYear(t);
    const newWeek = getISOWeek(t);
    setActiveYear(newYear);
    setActiveCalendarWeek(newWeek);
    // Falls "Heute" weit ausserhalb des Fensters: Fenster zentrieren auf heute
    const todayMon = getWeekMonday(t);
    if (todayMon < windowMonday || todayMon > windowSunday) {
      setWindowMonday(subDays(todayMon, WEEKS_INITIAL_BEFORE * 7));
      setWindowSunday(addDays(todayMon, WEEKS_INITIAL_AFTER * 7 - 1));
    }
    // Direkt scrollen (falls Fenster bereits korrekt ist)
    setTimeout(() => {
      const c = scrollContainerRef.current;
      if (!c) return;
      const dayIdx = differenceInDays(todayMon, windowMonday);
      const targetLeft = dayIdx * DAY_WIDTH;
      c.scrollTo({ left: targetLeft, behavior: "smooth" });
    }, 60);
  }, [windowMonday, windowSunday, DAY_WIDTH]);
  const handleYearChange = useCallback((y) => { setActiveYear(y); const max = getMaxISOWeeks(y); if (activeCalendarWeek > max) setActiveCalendarWeek(max); }, [activeCalendarWeek]);
  const handleWeekChange = useCallback((w) => setActiveCalendarWeek(w), []);

  // ============ LOAD DATA ============
  useEffect(() => {
    Promise.all([authFetch(`${API}/users`), authFetch(`${API}/projects`), authFetch(`${API}/week-templates`)]).then(async ([u, p, t]) => {
      if (u.ok) setUsers((await u.json()).filter(x => x.is_active !== false));
      if (p.ok) {
        const all = await p.json();
        // Nur nicht-archivierte und nicht-inaktive Projekte für die Einsatzplanung
        setProjects(all.filter(x =>
          x.is_active !== false &&
          x.status !== "archiviert" &&
          x.status !== "inaktiv"
        ));
      }
      setTemplates(t.ok ? await t.json() : []);
    }).catch(console.error);
  }, []);

  const loadAssignments = useCallback(async (forceReload = false) => {
    if (hasChanges && !forceReload) return;
    if (visibleDays.length === 0) return;
    const rangeStart = visibleDays[0], rangeEnd = visibleDays[visibleDays.length - 1];
    if (!forceReload && loadedRange.start && loadedRange.end && rangeStart >= loadedRange.start && rangeEnd <= loadedRange.end) return;
    setLoading(true);
    try {
      const loadStart = subDays(rangeStart, 7), loadEnd = addDays(rangeEnd, 7);
      let url = `${API}/assignments?from=${format(loadStart, "yyyy-MM-dd")}&to=${format(loadEnd, "yyyy-MM-dd")}`;
      const effectiveProjectId = projectId || filterProjectId;
      if (effectiveProjectId) url += `&project_id=${effectiveProjectId}`;
      if (filterUserIds.length > 0) url += `&user_ids=${filterUserIds.join(",")}`;
      let sollUrl = `${API}/assignments/soll-ist?from=${format(loadStart, "yyyy-MM-dd")}&to=${format(loadEnd, "yyyy-MM-dd")}`;
      if (effectiveProjectId) sollUrl += `&project_id=${effectiveProjectId}`;
      const [aRes, sRes] = await Promise.all([authFetch(url), authFetch(sollUrl)]);
      if (aRes.ok) { const d = await aRes.json(); setAssignments(d); setOriginalAssignments(JSON.parse(JSON.stringify(d))); setLoadedRange({ start: loadStart, end: loadEnd }); }
      if (sRes.ok) setSollIstData(await sRes.json());
    } catch { toast.error("Fehler beim Laden"); } finally { setLoading(false); }
  }, [visibleDays, filterUserIds, filterProjectId, projectId, hasChanges, loadedRange]);

  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  // Auto-refresh assignments every 90s (only when no pending changes)
  useEffect(() => {
    const interval = setInterval(() => {
      if (!hasChanges) loadAssignments(true);
    }, 90000);
    return () => clearInterval(interval);
  }, [hasChanges, loadAssignments]);

  // ============ SCROLL ============
  useEffect(() => {
    const m = () => { if (scrollContainerRef.current) setScrollbarWidth(scrollContainerRef.current.offsetWidth - scrollContainerRef.current.clientWidth); };
    m(); window.addEventListener("resize", m); return () => window.removeEventListener("resize", m);
  }, []);

  // Initial scroll to today on mount (after grid renders)
  const initialScrollDoneRef = useRef(false);
  useEffect(() => {
    if (initialScrollDoneRef.current) return;
    if (visibleDays.length === 0) return;
    
    // Poll for the scroll container to be ready
    const checkAndScroll = () => {
      const c = scrollContainerRef.current;
      if (!c) {
        // Ref not ready, try again
        setTimeout(checkAndScroll, 50);
        return;
      }
      if (c.scrollWidth <= c.clientWidth) {
        // Content not rendered yet, try again
        setTimeout(checkAndScroll, 50);
        return;
      }
      // Block scroll handler during initial scroll
      extendingRef.current = true;
      // Calculate position of today (4 weeks from start = WEEKS_INITIAL_BEFORE * 7 days)
      const todayIdx = differenceInDays(initialMonday, windowMonday);
      const targetLeft = todayIdx * DAY_WIDTH;
      // Only scroll if we're at position 0 (initial state)
      if (c.scrollLeft < 100) {
        c.scrollLeft = targetLeft;
      }
      initialScrollDoneRef.current = true;
      // Release scroll handler after a short delay
      setTimeout(() => { extendingRef.current = false; }, 100);
    };
    
    // Start checking after a short delay to let React render
    const t = setTimeout(checkAndScroll, 100);
    return () => clearTimeout(t);
  }, [visibleDays.length, windowMonday, initialMonday, DAY_WIDTH]);

  useEffect(() => {
    const h = (e) => { if (employeeFilterRef.current && !employeeFilterRef.current.contains(e.target)) setEmployeeFilterOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setEmployeeFilterOpen(false); };
    if (employeeFilterOpen) { document.addEventListener("mousedown", h); document.addEventListener("keydown", esc); }
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("keydown", esc); };
  }, [employeeFilterOpen]);

  const handleScroll = useCallback(() => {
    const c = scrollContainerRef.current;
    if (!c || extendingRef.current) return;
    const scrollLeft = c.scrollLeft;
    const scrollMax = c.scrollWidth - c.clientWidth;
    if (scrollMax <= 0) return;
    if (scrollLeft < SCROLL_NEAR_EDGE_PX) {
      // Links erweitern: 4 Wochen prepend, scrollLeft korrigieren via useLayoutEffect
      extendingRef.current = true;
      pendingPrependDaysRef.current += WEEKS_EXTEND_CHUNK * 7;
      setWindowMonday(prev => subDays(prev, WEEKS_EXTEND_CHUNK * 7));
      // Lock kurz, bis Layout stabilisiert (useLayoutEffect setzt Flag zurück)
    } else if (scrollMax - scrollLeft < SCROLL_NEAR_EDGE_PX) {
      // Rechts erweitern: 4 Wochen append, scrollLeft bleibt erhalten (rechts wird nur länger)
      extendingRef.current = true;
      setWindowSunday(prev => addDays(prev, WEEKS_EXTEND_CHUNK * 7));
      // Append benötigt kein Korrektur-Layout; Lock nach kurzem Tick lösen
      setTimeout(() => { extendingRef.current = false; }, 80);
    }
  }, []);

  // Nach Prepend: Scroll-Position anchoren (anti-jump), dann Lock lösen.
  useLayoutEffect(() => {
    if (pendingPrependDaysRef.current > 0 && scrollContainerRef.current) {
      const c = scrollContainerRef.current;
      c.scrollLeft += pendingPrependDaysRef.current * DAY_WIDTH;
      pendingPrependDaysRef.current = 0;
      // Mini-Tick warten, damit nächster Scroll-Event nicht sofort wieder triggert
      setTimeout(() => { extendingRef.current = false; }, 50);
    }
  }, [windowMonday, DAY_WIDTH]);

  // Beim Wechsel der aktiven KW (Header-Pfeile, Heute, Picker): Sicherstellen, dass
  // activeWeekMonday im Fenster liegt + sanft dorthin scrollen.
  useEffect(() => {
    if (!scrollContainerRef.current) return;
    // Ausserhalb? → Fenster anpassen, dann beim nächsten Render scrollen.
    if (activeWeekMonday < windowMonday) {
      pendingPrependDaysRef.current += differenceInDays(windowMonday, activeWeekMonday) + WEEKS_EXTEND_CHUNK * 7;
      setWindowMonday(subDays(activeWeekMonday, WEEKS_EXTEND_CHUNK * 7));
      return;
    }
    if (activeWeekMonday > windowSunday) {
      setWindowSunday(addDays(activeWeekMonday, WEEKS_EXTEND_CHUNK * 7));
      return;
    }
    const dayIdx = differenceInDays(activeWeekMonday, windowMonday);
    const t = setTimeout(() => {
      const c = scrollContainerRef.current;
      if (!c) return;
      // Smooth nur, wenn Differenz moderat ist – große Sprünge instant, um Lag zu vermeiden.
      const targetLeft = dayIdx * DAY_WIDTH;
      const diff = Math.abs(c.scrollLeft - targetLeft);
      c.scrollTo({ left: targetLeft, behavior: diff > 1500 ? "auto" : "smooth" });
    }, 60);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeYear, activeCalendarWeek]);

  // ============ GRAB-TO-SCROLL ============
  const handleGrabStart = useCallback((e) => {
    if (e.target.closest('[draggable="true"]') || e.target.closest("button, input, select") || e.target.classList.contains("cursor-ew-resize") || e.button !== 0) return;
    isGrabbingRef.current = true; setIsGrabbing(true);
    grabStartX.current = e.clientX; grabScrollStartX.current = scrollContainerRef.current?.scrollLeft || 0;
    e.preventDefault();
  }, []);

  const handleGrabMove = useCallback((e) => {
    if (!isGrabbingRef.current) return;
    const newLeft = grabScrollStartX.current - (e.clientX - grabStartX.current);
    if (scrollContainerRef.current) { scrollContainerRef.current.scrollLeft = newLeft; }
  }, []);

  const handleGrabEnd = useCallback(() => { if (isGrabbingRef.current) { isGrabbingRef.current = false; setIsGrabbing(false); } }, []);

  useEffect(() => {
    document.addEventListener("mousemove", handleGrabMove); document.addEventListener("mouseup", handleGrabEnd); document.addEventListener("mouseleave", handleGrabEnd);
    return () => { document.removeEventListener("mousemove", handleGrabMove); document.removeEventListener("mouseup", handleGrabEnd); document.removeEventListener("mouseleave", handleGrabEnd); };
  }, [handleGrabMove, handleGrabEnd]);

  const handleWheel = useCallback((e) => {
    if (!scrollContainerRef.current) return;
    if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      e.preventDefault(); const d = e.deltaX || e.deltaY; scrollContainerRef.current.scrollLeft += d;
    }
  }, []);

  // ============ MULTI-SELECT ============
  const handleSelectAssignment = useCallback((id, isMulti) => {
    if (id === null) { setSelectedIds(new Set()); return; }
    setSelectedIds(prev => { const n = new Set(isMulti ? prev : []); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const handleQuickDelete = useCallback((a) => setQuickDeleteTarget(a), []);

  const confirmQuickDelete = useCallback(() => {
    if (!quickDeleteTarget) return;
    if (quickDeleteTarget.id.startsWith("temp-")) setPendingChanges(p => { const c = new Map(p.created); c.delete(quickDeleteTarget.id); return { ...p, created: c }; });
    else setPendingChanges(p => { const d = new Set(p.deleted); d.add(quickDeleteTarget.id); const m = new Map(p.modified); m.delete(quickDeleteTarget.id); return { ...p, deleted: d, modified: m }; });
    setQuickDeleteTarget(null); setSelectedIds(prev => { const n = new Set(prev); n.delete(quickDeleteTarget.id); return n; });
  }, [quickDeleteTarget]);

  const handleBulkDelete = useCallback(() => {
    if (selectedIds.size === 0) return;
    setPendingChanges(p => {
      const c = new Map(p.created), d = new Set(p.deleted), m = new Map(p.modified);
      for (const id of selectedIds) { if (id.startsWith("temp-")) c.delete(id); else { d.add(id); m.delete(id); } }
      return { ...p, created: c, deleted: d, modified: m };
    });
    toast.success(`${selectedIds.size} gel\u00f6scht`); setSelectedIds(new Set());
  }, [selectedIds]);

  // ============ ASSIGNMENT HELPERS ============
  const getEffectiveAssignments = useCallback(() => {
    let result = assignments.map(a => pendingChanges.modified.has(a.id) ? { ...a, ...pendingChanges.modified.get(a.id) } : a);
    pendingChanges.created.forEach((a, tempId) => { result.push({ ...a, id: tempId }); });
    const siMap = new Map(sollIstData.map(s => [s.id, s]));
    result = result.map(a => { const si = siMap.get(a.id); return si ? { ...a, actual_hours: si.actual_hours, delta_hours: si.delta_hours, delta_status: si.delta_status } : a; });
    return result;
  }, [assignments, pendingChanges, sollIstData]);

  const getAssignmentsForCell = useCallback((userId, date) => {
    const ds = format(date, "yyyy-MM-dd");
    return getEffectiveAssignments().filter(a => a.user_id === userId && a.date === ds);
  }, [getEffectiveAssignments]);

  const getTotalHours = useCallback((userId, date) => {
    return getAssignmentsForCell(userId, date).filter(a => !pendingChanges.deleted.has(a.id)).reduce((s, a) => s + a.planned_hours, 0);
  }, [getAssignmentsForCell, pendingChanges.deleted]);

  const hasDayBlockedStatus = useCallback((userId, date) => {
    return getAssignmentsForCell(userId, date).some(a => a.status === "blocked" && !pendingChanges.deleted.has(a.id));
  }, [getAssignmentsForCell, pendingChanges.deleted]);

  const validateDrop = useCallback((targetDate, targetUserId, assignment) => {
    if (!assignment) return { valid: false, reason: "Kein Einsatz" };
    if (hasDayBlockedStatus(targetUserId, targetDate)) return { valid: false, reason: "Blockiert" };
    const newH = getTotalHours(targetUserId, targetDate) + assignment.planned_hours;
    if (newH > DEFAULT_DAILY_CAPACITY) return { valid: true, reason: `\u00dcberbuchung: ${newH}h`, warning: true };
    return { valid: true, reason: "" };
  }, [getTotalHours, hasDayBlockedStatus]);

  // ============ DIALOG HANDLERS ============
  const handleCellSelect = useCallback((date, userId) => setSelectedCell({ date, userId }), []);

  const handleAddClick = useCallback((date, userId = "") => {
    setSelectedCell(null); setSelectedDate(date); setSelectedUserId(userId); setSelectedAssignment(null);
    setFormData({ user_id: userId || (users[0]?.id || ""), project_id: projects[0]?.id || "", planned_hours: 8, status: "planned", notes: "" });
    setDialogOpen(true);
  }, [users, projects]);

  const handleAssignmentClick = useCallback((a) => {
    if (pendingChanges.deleted.has(a.id)) return;
    setSelectedCell(null); setSelectedAssignment(a); setSelectedDate(parseISO(a.date)); setSelectedUserId(a.user_id);
    setFormData({ user_id: a.user_id, project_id: a.project_id, planned_hours: a.planned_hours, status: a.status, notes: a.notes || "" });
    setDialogOpen(true);
  }, [pendingChanges.deleted]);

  const handleSave = useCallback(() => {
    const project = projects.find(p => p.id === formData.project_id);
    const userObj = users.find(u => u.id === formData.user_id);
    if (selectedAssignment) {
      const updated = { ...selectedAssignment, project_id: formData.project_id, project_name: project?.name || "", project_color: project?.color || "#6B7280", planned_hours: formData.planned_hours, status: formData.status, notes: formData.notes };
      setPendingChanges(p => { const m = new Map(p.modified); m.set(selectedAssignment.id, updated); return { ...p, modified: m }; });
    } else {
      const tempId = `temp-${Date.now()}`;
      const newA = { id: tempId, user_id: formData.user_id, user_name: userObj?.name || "", project_id: formData.project_id, project_name: project?.name || "", project_color: project?.color || "#6B7280", date: format(selectedDate, "yyyy-MM-dd"), planned_hours: formData.planned_hours, status: formData.status, notes: formData.notes };
      setPendingChanges(p => { const c = new Map(p.created); c.set(tempId, newA); return { ...p, created: c }; });
    }
    setDialogOpen(false);
  }, [selectedAssignment, formData, projects, users, selectedDate]);

  const handleDelete = useCallback(() => {
    if (!selectedAssignment) return;
    if (selectedAssignment.id.startsWith("temp-")) setPendingChanges(p => { const c = new Map(p.created); c.delete(selectedAssignment.id); return { ...p, created: c }; });
    else setPendingChanges(p => { const d = new Set(p.deleted); d.add(selectedAssignment.id); const m = new Map(p.modified); m.delete(selectedAssignment.id); return { ...p, deleted: d, modified: m }; });
    setDeleteDialogOpen(false); setDialogOpen(false);
  }, [selectedAssignment]);

  // ============ AUTO-SAVE ============
  const performAutoSave = useCallback(async (cs) => {
    if (cs.modified.size + cs.created.size + cs.deleted.size === 0) return;
    if (saveInFlightRef.current) return;
    saveInFlightRef.current = true; setSaveStatus("saving");
    try {
      const promises = [];
      for (const [id, a] of cs.modified) promises.push(authFetch(`${API}/assignments/${id}`, { method: "PATCH", body: JSON.stringify({ user_id: a.user_id, project_id: a.project_id, date: a.date, planned_hours: a.planned_hours, status: a.status, notes: a.notes }) }));
      for (const [, a] of cs.created) promises.push(authFetch(`${API}/assignments`, { method: "POST", body: JSON.stringify({ user_id: a.user_id, project_id: a.project_id, date: a.date, planned_hours: a.planned_hours, status: a.status, notes: a.notes }) }));
      for (const id of cs.deleted) promises.push(authFetch(`${API}/assignments/${id}`, { method: "DELETE" }));
      await Promise.all(promises);
      setPendingChanges({ modified: new Map(), created: new Map(), deleted: new Set() });
      setLoadedRange({ start: null, end: null }); await loadAssignments(true);
      setSaveStatus("saved"); setTimeout(() => setSaveStatus("idle"), 1500);
    } catch { setSaveStatus("error"); toast.error("Fehler beim Speichern"); setTimeout(() => setSaveStatus("idle"), 3000); }
    finally { saveInFlightRef.current = false; }
  }, [loadAssignments]);

  useEffect(() => {
    if (totalChanges === 0) return;
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = setTimeout(() => {
      performAutoSave({ modified: new Map(pendingChanges.modified), created: new Map(pendingChanges.created), deleted: new Set(pendingChanges.deleted) });
    }, AUTO_SAVE_DELAY);
    return () => { if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current); };
  }, [pendingChanges, performAutoSave, totalChanges]);

  const discardAllChanges = useCallback(() => {
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    setPendingChanges({ modified: new Map(), created: new Map(), deleted: new Set() });
    setAssignments(JSON.parse(JSON.stringify(originalAssignments)));
    setDiscardDialogOpen(false); setSaveStatus("idle"); toast.info("Verworfen");
  }, [originalAssignments]);

  // ============ DRAG & DROP ============
  const handleDragStart = useCallback((e, a) => { e.dataTransfer.effectAllowed = "copyMove"; setDraggedAssignment(a); setIsCopyDrag(e.ctrlKey || e.metaKey); }, []);
  const handleDragEnd = useCallback(() => { setDraggedAssignment(null); setDragOverDate(null); setDragOverUserId(null); setIsCopyDrag(false); }, []);
  const handleDragOver = useCallback((e, date, userId) => { e.preventDefault(); if (!draggedAssignment) return; setDragOverDate(date); setDragOverUserId(userId); }, [draggedAssignment]);

  const handleDrop = useCallback((e, targetDate, targetUserId) => {
    e.preventDefault(); if (!draggedAssignment) return;
    const v = validateDrop(targetDate, targetUserId, draggedAssignment);
    if (!v.valid) { toast.error(v.reason); handleDragEnd(); return; }
    if (v.warning) toast.warning(v.reason);
    const ds = format(targetDate, "yyyy-MM-dd");
    const isCopy = e.ctrlKey || e.metaKey || isCopyDrag;
    if (isCopy) {
      const tid = `temp-${Date.now()}`;
      const na = { ...draggedAssignment, id: tid, user_id: targetUserId, user_name: users.find(u => u.id === targetUserId)?.name || "", date: ds };
      setPendingChanges(p => { const c = new Map(p.created); c.set(tid, na); return { ...p, created: c }; });
    } else {
      const ua = { ...draggedAssignment, user_id: targetUserId, user_name: users.find(u => u.id === targetUserId)?.name || "", date: ds };
      if (draggedAssignment.id.startsWith("temp-")) setPendingChanges(p => { const c = new Map(p.created); c.set(draggedAssignment.id, ua); return { ...p, created: c }; });
      else setPendingChanges(p => { const m = new Map(p.modified); m.set(draggedAssignment.id, ua); return { ...p, modified: m }; });
    }
    handleDragEnd();
  }, [draggedAssignment, isCopyDrag, users, validateDrop, handleDragEnd]);

  // ============ RESIZE ============
  const handleResizeMove = useCallback((e) => {
    if (!resizingAssignment) return;
    const deltaH = ((e.clientX - resizeStartX) / 50) * RESIZE_SNAP;
    let nh = resizeDirection === "right" ? resizeStartHours + deltaH : resizeStartHours - deltaH;
    nh = Math.round(nh / RESIZE_SNAP) * RESIZE_SNAP;
    setResizePreviewHours(Math.max(RESIZE_SNAP, Math.min(24, nh)));
  }, [resizingAssignment, resizeStartX, resizeStartHours, resizeDirection]);

  const handleResizeEnd = useCallback(() => {
    if (resizingAssignment && resizePreviewHours !== resizeStartHours) {
      const ua = { ...resizingAssignment, planned_hours: resizePreviewHours };
      if (resizingAssignment.id.startsWith("temp-")) setPendingChanges(p => { const c = new Map(p.created); c.set(resizingAssignment.id, ua); return { ...p, created: c }; });
      else setPendingChanges(p => { const m = new Map(p.modified); m.set(resizingAssignment.id, ua); return { ...p, modified: m }; });
    }
    setResizingAssignment(null); setResizeDirection(null); setResizePreviewHours(0);
    document.removeEventListener("mousemove", handleResizeMove);
    document.removeEventListener("mouseup", handleResizeEnd);
  }, [resizingAssignment, resizePreviewHours, resizeStartHours, handleResizeMove]);

  const handleResizeStart = useCallback((e, a, dir) => {
    e.stopPropagation(); setResizingAssignment(a); setResizeDirection(dir); setResizeStartX(e.clientX);
    setResizeStartHours(a.planned_hours); setResizePreviewHours(a.planned_hours);
    document.addEventListener("mousemove", handleResizeMove);
    document.addEventListener("mouseup", handleResizeEnd);
  }, [handleResizeMove, handleResizeEnd]);

  // ============ FILL HANDLE ============
  const handleFillHandleStart = useCallback((e, a) => {
    e.preventDefault(); e.stopPropagation();
    setFillDragAssignment(a); setIsFillDragging(true); setFillPreviewCells([]);
    fillDragRef.current = { startX: e.clientX, startY: e.clientY, assignment: a };
  }, []);

  const handleFillHandleMove = useCallback((e) => {
    if (!isFillDragging || !fillDragRef.current) return;
    const { assignment } = fillDragRef.current;
    const daysDelta = Math.round((e.clientX - fillDragRef.current.startX) / DAY_WIDTH);
    const cells = [];
    if (daysDelta !== 0) {
      const dir = daysDelta > 0 ? 1 : -1;
      for (let i = 1; i <= Math.abs(daysDelta); i++) {
        const td = addDays(parseISO(assignment.date), i * dir);
        const tds = format(td, "yyyy-MM-dd");
        if (!assignments.find(a => a.user_id === assignment.user_id && a.date === tds)) cells.push({ date: td, userId: assignment.user_id });
      }
    }
    setFillPreviewCells(cells);
  }, [isFillDragging, assignments]);

  const handleFillHandleEnd = useCallback(() => {
    if (!isFillDragging || !fillDragAssignment || fillPreviewCells.length === 0) {
      setIsFillDragging(false); setFillDragAssignment(null); setFillPreviewCells([]); return;
    }
    const valid = fillPreviewCells.filter(c => !assignments.find(a => a.user_id === c.userId && a.date === format(c.date, "yyyy-MM-dd")));
    if (valid.length === 0) { toast.warning("Zellen belegt"); setIsFillDragging(false); setFillDragAssignment(null); setFillPreviewCells([]); return; }
    const project = projects.find(p => p.id === fillDragAssignment.project_id);
    valid.forEach((cell, i) => {
      const nid = `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`;
      const na = { id: nid, user_id: cell.userId, user_name: users.find(u => u.id === cell.userId)?.name || "", project_id: fillDragAssignment.project_id, project_name: project?.name || "", project_color: project?.color || "#6B7280", date: format(cell.date, "yyyy-MM-dd"), planned_hours: fillDragAssignment.planned_hours, status: fillDragAssignment.status || "planned", notes: fillDragAssignment.notes || "" };
      setAssignments(prev => [...prev, na]);
      setPendingChanges(p => ({ ...p, created: new Map(p.created).set(nid, na) }));
    });
    setIsFillDragging(false); setFillDragAssignment(null); setFillPreviewCells([]);
  }, [isFillDragging, fillDragAssignment, fillPreviewCells, projects, users, assignments]);

  useEffect(() => {
    if (isFillDragging) { document.addEventListener("mousemove", handleFillHandleMove); document.addEventListener("mouseup", handleFillHandleEnd); }
    return () => { document.removeEventListener("mousemove", handleFillHandleMove); document.removeEventListener("mouseup", handleFillHandleEnd); };
  }, [isFillDragging, handleFillHandleMove, handleFillHandleEnd]);

  // ============ TEMPLATE HANDLERS ============
  const handleSaveTemplate = useCallback(async () => {
    if (!templateName.trim()) { toast.error("Name fehlt"); return; }
    try {
      const r = await authFetch(`${API}/week-templates?name=${encodeURIComponent(templateName)}&week_start=${currentWeekStart}`, { method: "POST" });
      if (r.ok) { const newT = await r.json(); setTemplates(p => [newT, ...p]); toast.success("Vorlage gespeichert"); setTemplateDialogOpen(false); setTemplateName(""); }
      else { const e = await r.json(); toast.error(e.detail || "Fehler"); }
    } catch { toast.error("Fehler"); }
  }, [templateName, currentWeekStart]);

  const handleApplyTemplate = useCallback(async () => {
    if (!selectedTemplateId || !targetWeekStart) { toast.error("Vorlage/Zielwoche fehlt"); return; }
    try {
      const r = await authFetch(`${API}/week-templates/${selectedTemplateId}/apply?target_week_start=${targetWeekStart}`, { method: "POST" });
      if (r.ok) { toast.success((await r.json()).message); setApplyTemplateDialogOpen(false); setLoadedRange({ start: null, end: null }); await loadAssignments(true); }
      else toast.error((await r.json()).detail || "Fehler");
    } catch { toast.error("Fehler"); }
  }, [selectedTemplateId, targetWeekStart, loadAssignments]);

  // ============ RECURRING TEMPLATE HANDLERS ============
  const handleToggleRecurring = useCallback(async (templateId, recurring, interval = "weekly") => {
    try {
      const r = await authFetch(`${API}/week-templates/${templateId}/recurring?recurring=${recurring}&interval=${interval}`, { method: "PUT" });
      if (r.ok) {
        const updated = await r.json();
        setTemplates(prev => prev.map(t => t.id === templateId ? updated : t));
        toast.success(recurring ? `Vorlage auf "${interval === "weekly" ? "Woechentlich" : "Alle 2 Wochen"}" gesetzt` : "Wiederholung deaktiviert");
      } else {
        toast.error((await r.json()).detail || "Fehler");
      }
    } catch { toast.error("Fehler beim Aktualisieren"); }
  }, []);

  const handleGenerateNext = useCallback(async (templateId) => {
    setGeneratingId(templateId);
    try {
      const r = await authFetch(`${API}/week-templates/${templateId}/generate-next`, { method: "POST" });
      if (r.ok) {
        const data = await r.json();
        toast.success(data.message);
        setTemplates(prev => prev.map(t => {
          if (t.id === templateId) return { ...t, last_applied_date: data.week_start, next_apply_date: undefined };
          return t;
        }));
        // Reload templates to get updated next_apply_date
        const tRes = await authFetch(`${API}/week-templates`);
        if (tRes.ok) setTemplates(await tRes.json());
        setLoadedRange({ start: null, end: null });
        await loadAssignments(true);
      } else {
        const err = await r.json();
        toast.error(err.detail || "Fehler beim Generieren");
      }
    } catch { toast.error("Fehler beim Generieren"); }
    finally { setGeneratingId(null); }
  }, [loadAssignments]);

  const handleDeleteTemplate = useCallback(async (templateId) => {
    try {
      const r = await authFetch(`${API}/week-templates/${templateId}`, { method: "DELETE" });
      if (r.ok) {
        setTemplates(prev => prev.filter(t => t.id !== templateId));
        toast.success("Vorlage geloescht");
      } else toast.error("Fehler beim Loeschen");
    } catch { toast.error("Fehler"); }
  }, []);

  // ============ PDF EXPORT ============
  const handlePdfExport = useCallback(async () => {
    if (!pdfDateRange.start || !pdfDateRange.end) { toast.error("Zeitraum w\u00e4hlen"); return; }
    setPdfExportLoading(true);
    try {
      const r = await authFetch(`${API}/assignments/pdf`, { method: "POST", body: JSON.stringify({ start_date: pdfDateRange.start, end_date: pdfDateRange.end, filter_user_ids: pdfUseFilters && filterUserIds.length > 0 ? filterUserIds : null, filter_project_id: pdfUseFilters && filterProjectId ? filterProjectId : null }) });
      if (r.ok) { const b = await r.blob(); const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = `Einsatzplanung_${pdfDateRange.start}_${pdfDateRange.end}.pdf`; document.body.appendChild(a); a.click(); URL.revokeObjectURL(u); a.remove(); toast.success("PDF erstellt"); setPdfExportOpen(false); }
      else toast.error((await r.json()).detail || "Fehler");
    } catch { toast.error("Fehler beim PDF"); } finally { setPdfExportLoading(false); }
  }, [pdfDateRange, pdfUseFilters, filterUserIds, filterProjectId]);

  // ============ KEYBOARD ============
  useEffect(() => {
    const h = (e) => {
      if (!dialogOpen && !templateDialogOpen && !applyTemplateDialogOpen) {
        if (e.key === "h" || e.key === "H") { e.preventDefault(); goToToday(); }
        if (e.key === "ArrowLeft" && e.ctrlKey) { e.preventDefault(); goToPreviousWeek(); }
        if (e.key === "ArrowRight" && e.ctrlKey) { e.preventDefault(); goToNextWeek(); }
      }
      if (e.key === "Escape") {
        if (isGrabbingRef.current) handleGrabEnd();
        if (draggedAssignment) { setDraggedAssignment(null); setDragOverDate(null); setDragOverUserId(null); setIsCopyDrag(false); }
        if (resizingAssignment) { setResizingAssignment(null); setResizeDirection(null); setResizePreviewHours(0); }
        if (selectedIds.size > 0 && !dialogOpen) clearSelection();
      }
      if (e.key === "Delete" && !dialogOpen && selectedIds.size > 0) handleBulkDelete();
      if (e.key === "Delete" && dialogOpen && selectedAssignment) setDeleteDialogOpen(true);
    };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, [dialogOpen, templateDialogOpen, applyTemplateDialogOpen, goToToday, goToPreviousWeek, goToNextWeek, handleGrabEnd, draggedAssignment, resizingAssignment, selectedIds, handleBulkDelete, clearSelection, selectedAssignment]);

  // Warn before leaving
  useEffect(() => {
    const h = (e) => { if (hasChanges) { e.preventDefault(); e.returnValue = "Ungespeicherte \u00c4nderungen"; return e.returnValue; } };
    window.addEventListener("beforeunload", h); return () => window.removeEventListener("beforeunload", h);
  }, [hasChanges]);

  // Filtered users
  const displayUsers = filterUserIds.length > 0 ? users.filter(u => filterUserIds.includes(u.id)) : users;

  // Access check
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertTriangle className="w-10 h-10 text-yellow-500 mb-3" />
        <h2 className="text-base font-semibold text-gray-900 mb-1">Zugriff verweigert</h2>
        <p className="text-[12px] text-gray-500">Nur Administratoren/Projektleiter.</p>
      </div>
    );
  }

  const today = startOfDay(new Date());
  const dropValidation = draggedAssignment && dragOverDate && dragOverUserId ? validateDrop(dragOverDate, dragOverUserId, draggedAssignment) : { valid: true };

  return (
    <div className="h-[calc(100vh-100px)] flex flex-col">
      <PlanningHeader
        isGlobalMode={isGlobalMode} activeProject={activeProject}
        activeYear={activeYear} activeCalendarWeek={activeCalendarWeek} dateRangeText={dateRangeText}
        saveStatus={saveStatus} showSollIst={showSollIst} setShowSollIst={setShowSollIst}
        goToPreviousWeek={goToPreviousWeek} goToNextWeek={goToNextWeek} goToToday={goToToday}
        handleYearChange={handleYearChange} handleWeekChange={handleWeekChange}
        filterUserIds={filterUserIds} setFilterUserIds={setFilterUserIds}
        filterProjectId={filterProjectId} setFilterProjectId={setFilterProjectId}
        users={users} projects={projects}
        employeeFilterOpen={employeeFilterOpen} setEmployeeFilterOpen={setEmployeeFilterOpen}
        tempFilterUserIds={tempFilterUserIds} setTempFilterUserIds={setTempFilterUserIds}
        employeeSearch={employeeSearch} setEmployeeSearch={setEmployeeSearch}
        employeeFilterRef={employeeFilterRef}
        handleAddClick={handleAddClick} today={today}
        onPdfExport={() => { const ws = visibleDays[7], we = visibleDays[13]; setPdfDateRange({ start: format(ws, "yyyy-MM-dd"), end: format(we, "yyyy-MM-dd") }); setPdfExportOpen(true); }}
        selectedIds={selectedIds} handleBulkDelete={handleBulkDelete} clearSelection={clearSelection}
        onSaveTemplate={() => setTemplateDialogOpen(true)}
        onApplyTemplate={() => setApplyTemplateDialogOpen(true)}
        onManageRecurring={() => setRecurringDialogOpen(true)}
        templates={templates}
        recurringCount={templates.filter(t => t.recurring && t.is_active).length}
        zoom={zoom}
        setZoom={(z) => { setZoom(z); localStorage.setItem("tga_planning_zoom", z); }}
      />

      <PlanningGrid
        visibleDays={visibleDays} weekGroups={weekGroups} displayUsers={displayUsers} today={today}
        activeCalendarWeek={activeCalendarWeek} activeYear={activeYear}
        scrollContainerRef={scrollContainerRef} headerScrollRef={headerScrollRef}
        scrollbarWidth={scrollbarWidth} handleScroll={handleScroll} handleWheel={handleWheel}
        handleGrabStart={handleGrabStart} isGrabbing={isGrabbing}
        getAssignmentsForCell={getAssignmentsForCell} getTotalHours={getTotalHours} hasDayBlockedStatus={hasDayBlockedStatus}
        handleDragStart={handleDragStart} handleDragEnd={handleDragEnd} handleDragOver={handleDragOver} handleDrop={handleDrop}
        handleResizeStart={handleResizeStart} handleFillHandleStart={handleFillHandleStart}
        dragOverDate={dragOverDate} dragOverUserId={dragOverUserId} dropValidation={dropValidation}
        resizingAssignment={resizingAssignment} resizePreviewHours={resizePreviewHours}
        modifiedIds={modifiedIds} newIds={newIds} deletedIds={deletedIds}
        showSollIst={showSollIst} isFillDragging={isFillDragging} fillPreviewCells={fillPreviewCells}
        selectedCell={selectedCell} selectedIds={selectedIds}
        handleAddClick={handleAddClick} handleAssignmentClick={handleAssignmentClick}
        handleCellSelect={handleCellSelect} handleSelectAssignment={handleSelectAssignment} handleQuickDelete={handleQuickDelete}
        loading={loading} assignmentsLength={assignments.length}
        zoom={zoom}
        onOpenInTimesheet={(a) => {
          // User-Wunsch 05.05.2026: Klick auf Einsatz oeffnet Stundenkonto
          // mit Filter auf user_id, date, project_id
          const params = new URLSearchParams();
          if (a?.user_id) params.set("user", a.user_id);
          if (a?.date) params.set("date", a.date);
          if (a?.project_id) params.set("project", a.project_id);
          navigate(`/app/stundenkonten?${params.toString()}`);
        }}
      />

      {/* Dialogs */}
      <AssignmentDialog open={dialogOpen} onOpenChange={setDialogOpen}
        selectedAssignment={selectedAssignment} selectedDate={selectedDate}
        formData={formData} setFormData={setFormData} users={users} projects={projects}
        onSave={handleSave} onDeleteOpen={setDeleteDialogOpen} />

      <QuickDeleteDialog target={quickDeleteTarget} onClose={() => setQuickDeleteTarget(null)} onConfirm={confirmQuickDelete} />
      <DeleteConfirmDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} onDelete={handleDelete} />
      <DiscardDialog open={discardDialogOpen} onOpenChange={setDiscardDialogOpen} onDiscard={discardAllChanges}
        modifiedCount={modifiedCount} createdCount={createdCount} deletedCount={deletedCount} />
      <SaveTemplateDialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}
        templateName={templateName} setTemplateName={setTemplateName} onSave={handleSaveTemplate} activeCalendarWeek={activeCalendarWeek} />
      <ApplyTemplateDialog open={applyTemplateDialogOpen} onOpenChange={setApplyTemplateDialogOpen}
        templates={templates} selectedTemplateId={selectedTemplateId} setSelectedTemplateId={setSelectedTemplateId}
        targetWeekStart={targetWeekStart} setTargetWeekStart={setTargetWeekStart} onApply={handleApplyTemplate} />
      <PdfExportDialog open={pdfExportOpen} onOpenChange={setPdfExportOpen}
        pdfDateRange={pdfDateRange} setPdfDateRange={setPdfDateRange}
        pdfUseFilters={pdfUseFilters} setPdfUseFilters={setPdfUseFilters}
        filterUserIds={filterUserIds} pdfExportLoading={pdfExportLoading} onExport={handlePdfExport} />
      <RecurringTemplatesDialog
        open={recurringDialogOpen} onOpenChange={setRecurringDialogOpen}
        templates={templates}
        onToggleRecurring={handleToggleRecurring}
        onGenerateNext={handleGenerateNext}
        onDeleteTemplate={handleDeleteTemplate}
        generatingId={generatingId}
      />
    </div>
  );
}
